//#region src/pwa/PwaInstallService.ts
var PwaInstallServiceImpl = class {
	constructor() {
		this._listeners = /* @__PURE__ */ new Set();
		this._installed = false;
		this._active = false;
		this._handleBeforeInstallPrompt = (event) => {
			event.preventDefault();
			this._deferredPrompt = event;
			this._notifyListeners();
		};
		this._handleAppInstalled = () => {
			this._deferredPrompt = void 0;
			this._installed = true;
			this._notifyListeners();
		};
		this.activate();
	}
	/**
	* Attach the window listeners. Idempotent and SSR-safe. Called from the
	* constructor and again from `startInit` to guarantee the listeners are in
	* place early enough to catch `beforeinstallprompt` (which fires once, early),
	* even if no component has imported the service yet.
	*/
	activate() {
		if (this._active || typeof window === "undefined") return;
		this._active = true;
		if (this.isStandalone) this._installed = true;
		window.addEventListener("beforeinstallprompt", this._handleBeforeInstallPrompt);
		window.addEventListener("appinstalled", this._handleAppInstalled);
	}
	/** True when a deferred install prompt is available and not yet installed. */
	get canInstall() {
		return this._deferredPrompt !== void 0 && !this._installed;
	}
	/** True once the app has been installed (this session) or runs standalone. */
	get installed() {
		return this._installed;
	}
	/**
	* True when the browser has an install-prompt concept at all
	* ("onbeforeinstallprompt", Chromium). Lets consumers distinguish
	* "prompt not (yet) available" from "browser cannot install"
	* (e.g. desktop Firefox). iOS installs via the Share sheet instead —
	* check isIOS separately.
	*/
	get isSupported() {
		if (typeof window === "undefined") return false;
		return "onbeforeinstallprompt" in window;
	}
	/** True when the app is running in a standalone/installed display mode. */
	get isStandalone() {
		if (typeof window === "undefined") return false;
		const standaloneDisplay = window.matchMedia?.("(display-mode: standalone)").matches || window.matchMedia?.("(display-mode: minimal-ui)").matches || window.matchMedia?.("(display-mode: fullscreen)").matches;
		const iosStandalone = window.navigator.standalone === true;
		return Boolean(standaloneDisplay || iosStandalone);
	}
	/**
	* True on iOS Safari, where there is no programmatic install prompt and the
	* user must add the app via the Share sheet ("Add to Home Screen").
	*/
	get isIOS() {
		if (typeof window === "undefined") return false;
		if (this.isStandalone) return false;
		const ua = window.navigator.userAgent;
		const isIOSDevice = /iphone|ipad|ipod/i.test(ua);
		const isIPadOS = /macintosh/i.test(ua) && (window.navigator.maxTouchPoints ?? 0) > 1;
		return isIOSDevice || isIPadOS;
	}
	/** How the app can be installed in the current context. */
	get platform() {
		if (this._installed || this.isStandalone) return "installed";
		if (this.canInstall) return "installable";
		if (this.isIOS) return "ios";
		return "unsupported";
	}
	/**
	* Trigger the native install prompt. Resolves with the user's choice, or
	* `"unavailable"` when no deferred prompt exists (e.g. iOS or already
	* installed). A deferred prompt can only be used once.
	*/
	async promptInstall() {
		const deferred = this._deferredPrompt;
		if (!deferred) return "unavailable";
		this._deferredPrompt = void 0;
		this._notifyListeners();
		try {
			await deferred.prompt();
			const { outcome } = await deferred.userChoice;
			return outcome;
		} catch {
			return "unavailable";
		}
	}
	/**
	* Subscribe to installability changes (prompt captured, installed, consumed).
	* @returns Unsubscribe function.
	*/
	subscribe(callback) {
		this._listeners.add(callback);
		return () => this._listeners.delete(callback);
	}
	_notifyListeners() {
		this._listeners.forEach((cb) => cb());
	}
};
/** Singleton instance of the PWA install service. */
var pwaInstallService = new PwaInstallServiceImpl();
//#endregion
export { pwaInstallService };
